import useScheduleDataState from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useScheduleDataState.ts";
import useScheduleDataStateDispatch from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useScheduleDataStateDispatch.ts";
import { createSchedulesManagerLoader } from "@/pages/human-resources/time-management/services/CreateSchedulesManagerLoader.ts";
import type { SchedulesManager } from "@/pages/human-resources/time-management/business/SchedulesManager.ts";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import type { Site } from "@/shared/model/user/UserDomains.ts";
import { useCallback, useEffect, useState } from "react";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";

export default function useScheduleManagerLoader(
  currentSite: Site | undefined,
): SchedulesManager | undefined {
  const scheduleReferenceData = useScheduleReferenceData();
  const { visibleSitesIds } = useScheduleUserPermissions();
  const scheduleDataState = useScheduleDataState();
  const scheduleDataStateDispatch = useScheduleDataStateDispatch();
  const [schedulesManager, setSchedulesManager] = useState<
    SchedulesManager | undefined
  >(undefined);

  const handlePromisedManager = useCallback(
    async (promisedManager: Promise<SchedulesManager>) => {
      let manager: SchedulesManager | undefined = undefined;
      try {
        manager = await promisedManager;
      } catch (error: unknown) {
        console.error(error);
      } finally {
        setSchedulesManager(manager);
        scheduleDataStateDispatch({
          type: "contractsAndSchedulesSettled",
          errorOccurred: manager === undefined,
        });
      }
    },
    [scheduleDataStateDispatch],
  );

  useEffect(() => {
    const { createScheduleManager, updateScheduleManager } =
      createSchedulesManagerLoader(visibleSitesIds);
    if (
      currentSite === undefined ||
      scheduleDataState.startDate === undefined ||
      scheduleDataState.endDate === undefined
    ) {
      return;
    }

    if (scheduleDataState.contractsAreStale || schedulesManager === undefined) {
      void handlePromisedManager(
        createScheduleManager(
          scheduleReferenceData,
          scheduleDataState.startDate,
          scheduleDataState.endDate,
        ),
      );
    } else if (scheduleDataState.schedulesAreStale) {
      void handlePromisedManager(
        updateScheduleManager(scheduleReferenceData, schedulesManager),
      );
    }
  }, [
    currentSite,
    schedulesManager,
    handlePromisedManager,
    scheduleDataState.contractsAreStale,
    scheduleDataState.endDate,
    scheduleDataState.schedulesAreStale,
    scheduleDataState.startDate,
    scheduleReferenceData,
    visibleSitesIds,
  ]);

  return schedulesManager;
}
